import React, { useState } from 'react';
import { useKurs } from '@/hooks/useKurs';
import { DollarSign, Euro, Wifi, WifiOff, Check, X, Edit2, Loader2, RefreshCw, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function KursBar() {
  const {
    kurs, liveKurs, isManual, setManualKurs, isSaving, setLive, isSettingLive,
    kursEuro, liveKursEuro, isManualEuro, setManualKursEuro, isSavingEuro, setLiveEuro, isSettingLiveEuro
  } = useKurs();

  const [editingUsd, setEditingUsd] = useState(false);
  const [valUsd, setValUsd] = useState('');

  const [editingEur, setEditingEur] = useState(false);
  const [valEur, setValEur] = useState('');

  const openEditUsd = () => {
    setValUsd(String(kurs));
    setEditingUsd(true);
  };

  const handleSaveUsd = () => {
    const num = parseInt(valUsd, 10);
    if (num > 0) {
      setManualKurs(num);
      setEditingUsd(false);
    }
  };

  const handleLiveUsd = () => {
    setLive();
    setEditingUsd(false);
  };

  const openEditEur = () => {
    setValEur(String(kursEuro));
    setEditingEur(true);
  };

  const handleSaveEur = () => {
    const num = parseInt(valEur, 10);
    if (num > 0) {
      setManualKursEuro(num);
      setEditingEur(false);
    }
  };

  const handleLiveEur = () => {
    setLiveEuro();
    setEditingEur(false);
  };

  return (
    <div className="flex flex-row gap-3 w-full pb-2 hide-scrollbar justify-between">
      {/* USD Card */}
      <div className="bg-white border border-[#E6D5B8]/60 rounded-2xl md:min-w-[200px] min-w-0 shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden flex-1 p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-3 h-3 text-amber-500" />
            <span className="text-[9px] md:text-[10px] font-bold text-[#302621]">Kurs USD/IDR</span>
          </div>
          <div className="flex items-center gap-1">
            {isSettingLive && <RefreshCw className="w-2 h-2 text-slate-400 animate-spin" />}
            {isManual ? (
              <span className="flex items-center gap-0.5 text-[7px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                <WifiOff className="w-2 h-2" /> Manual
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[7px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                <Wifi className="w-2 h-2" /> Live Auto
              </span>
            )}
          </div>
        </div>

        <p className="text-[11px] md:text-xs font-black text-[#302621]">
          1USD = Rp {kurs.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>

        <div className="mt-2.5 flex items-center gap-1.5">
          {editingUsd ? (
            <div className="w-full space-y-1.5 animate-in fade-in slide-in-from-top-1">
              <p className="text-[8px] text-muted-foreground">
                Input kurs manual: {liveKurs && <span className="ml-1 text-green-600">Live: Rp {liveKurs.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
              </p>
              <div className="flex items-center gap-1.5">
                <Input 
                  type="number" 
                  value={valUsd} 
                  onChange={e => setValUsd(e.target.value)}
                  className="h-6 text-[10px] font-medium border-slate-300 rounded-md"
                  autoFocus
                />
                <Button size="icon" onClick={handleSaveUsd} className="h-6 w-6 rounded-md bg-green-600 hover:bg-green-700 shrink-0" disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-3 h-3 animate-spin text-white" /> : <Check className="w-3 h-3 text-white" />}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingUsd(false)} className="h-6 w-6 rounded-md text-slate-500 hover:bg-slate-100 shrink-0">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button 
                size="sm" 
                variant="outline"
                className={`flex-1 h-auto py-1 text-[8px] font-bold rounded-full gap-1 shadow-none ${!isManual ? 'bg-green-500 hover:bg-green-600 text-white border-0' : 'text-green-700 border-green-300 hover:bg-green-50'}`}
                onClick={handleLiveUsd}
                disabled={isSettingLive || !isManual}
              >
                <Wifi className="w-2.5 h-2.5" /> Live
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="flex-1 h-auto py-1 text-[8px] font-bold rounded-full gap-1 border-slate-200 text-[#7A695B] hover:bg-slate-50 shadow-sm"
                onClick={openEditUsd}
              >
                <Pencil className="w-2 h-2" /> Manual
              </Button>
            </>
          )}
        </div>
      </div>

      {/* EUR Card */}
      <div className="bg-white border border-[#E6D5B8]/60 rounded-2xl md:min-w-[200px] min-w-0 shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden flex-1 p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Euro className="w-3 h-3 text-blue-600" />
            <span className="text-[9px] md:text-[10px] font-bold text-[#302621]">Kurs EUR/IDR</span>
          </div>
          <div className="flex items-center gap-1">
            {isSettingLiveEuro && <RefreshCw className="w-2 h-2 text-slate-400 animate-spin" />}
            {isManualEuro ? (
              <span className="flex items-center gap-0.5 text-[7px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                <WifiOff className="w-2 h-2" /> Manual
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[7px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                <Wifi className="w-2 h-2" /> Live Auto
              </span>
            )}
          </div>
        </div>

        <p className="text-[11px] md:text-xs font-black text-[#302621]">
          1EUR = Rp {kursEuro.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>

        <div className="mt-2.5 flex items-center gap-1.5">
          {editingEur ? (
            <div className="w-full space-y-1.5 animate-in fade-in slide-in-from-top-1">
              <p className="text-[8px] text-muted-foreground">
                Input kurs manual: {liveKursEuro && <span className="ml-1 text-green-600">Live: Rp {liveKursEuro.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
              </p>
              <div className="flex items-center gap-1.5">
                <Input 
                  type="number" 
                  value={valEur} 
                  onChange={e => setValEur(e.target.value)}
                  className="h-6 text-[10px] font-medium border-slate-300 rounded-md"
                  autoFocus
                />
                <Button size="icon" onClick={handleSaveEur} className="h-6 w-6 rounded-md bg-green-600 hover:bg-green-700 shrink-0" disabled={isSavingEuro}>
                  {isSavingEuro ? <Loader2 className="w-3 h-3 animate-spin text-white" /> : <Check className="w-3 h-3 text-white" />}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingEur(false)} className="h-6 w-6 rounded-md text-slate-500 hover:bg-slate-100 shrink-0">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button 
                size="sm" 
                variant="outline"
                className={`flex-1 h-auto py-1 text-[8px] font-bold rounded-full gap-1 shadow-none ${!isManualEuro ? 'bg-green-500 hover:bg-green-600 text-white border-0' : 'text-green-700 border-green-300 hover:bg-green-50'}`}
                onClick={handleLiveEur}
                disabled={isSettingLiveEuro || !isManualEuro}
              >
                <Wifi className="w-2.5 h-2.5" /> Live
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="flex-1 h-auto py-1 text-[8px] font-bold rounded-full gap-1 border-slate-200 text-[#7A695B] hover:bg-slate-50 shadow-sm"
                onClick={openEditEur}
              >
                <Pencil className="w-2 h-2" /> Manual
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}